/**
 * Pure helpers for the API editor page — kept out of the component so they can be
 * unit-tested and so the page file stays about layout.
 */

export interface KeyValue {
  key: string;
  value: string;
}

/**
 * Insert `token` at the caret of an input/textarea (replacing any selection), or
 * append it when there's no live caret. Returns the new value plus the caret
 * position to restore afterwards. The caller sets the value through React, then
 * restores the caret on the next frame.
 */
export function insertAtCaret(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  current: string,
  token: string
): { value: string; caret: number } {
  const cur = current ?? '';
  if (!el || el.selectionStart == null) return { value: cur + token, caret: (cur + token).length };
  const start = el.selectionStart;
  const end = el.selectionEnd ?? start;
  return { value: cur.slice(0, start) + token + cur.slice(end), caret: start + token.length };
}

// ─── Endpoint URL <-> (baseUrl, path, query) ─────────────────────────────────

export interface SplitEndpoint {
  /** scheme + host (+ port) only — what the saved API locks the origin to. */
  baseUrl: string;
  /** Everything after the origin, before `?` — may contain {{vars}}. */
  path: string;
  queryParams: KeyValue[];
}

/**
 * Split a single user-typed endpoint (`https://api.x.com/v1/u/{{id}}?name={{name}}`)
 * into the pieces the backend stores. Done by hand — NOT via `new URL()` — so that
 * `{{variables}}` survive untouched instead of being percent-encoded.
 * Returns null when it isn't an http(s) URL or the host itself is templated (a
 * variable may never choose the host).
 */
export function splitEndpoint(raw: string): SplitEndpoint | null {
  const m = raw.trim().match(/^(https?:\/\/[^/?#]+)([^?#]*)(?:\?([^#]*))?(?:#.*)?$/i);
  if (!m || !m[1]) return null;
  if (m[1].includes('{') || m[1].includes('}')) return null;
  const query = m[3] ?? '';
  const queryParams: KeyValue[] = query
    ? query
        .split('&')
        .filter(Boolean)
        .map((kv) => {
          const i = kv.indexOf('=');
          return i < 0 ? { key: kv, value: '' } : { key: kv.slice(0, i), value: kv.slice(i + 1) };
        })
    : [];
  return { baseUrl: m[1], path: m[2] ?? '', queryParams };
}

/** Inverse of splitEndpoint — what the page shows in its single URL box. */
export function joinEndpoint(baseUrl: string, path: string | undefined, queryParams: KeyValue[] | undefined): string {
  const base = (baseUrl ?? '').replace(/\/+$/, '');
  const p = path ? (path.startsWith('/') ? path : `/${path}`) : '';
  const q = (queryParams ?? []).filter((k) => k.key).map((k) => `${k.key}=${k.value}`).join('&');
  return `${base}${p}${q ? `?${q}` : ''}`;
}

// ─── {{variable}} detection (drives the Test form) ───────────────────────────

const TOKEN_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

/** Unique `{{tokens}}` found in the given strings, in first-seen order. System
 *  tokens (`system.*`) and the response status vars are excluded — the server
 *  provides those, the tester has nothing to type for them. */
export function extractVariables(...sources: Array<string | undefined>): string[] {
  const seen = new Set<string>();
  for (const src of sources) {
    if (!src) continue;
    for (const m of src.matchAll(TOKEN_RE)) {
      const name = (m[1] ?? '').trim();
      if (!name || name.startsWith('system.') || name === '_httpStatus' || name === '_httpBody') continue;
      seen.add(name);
    }
  }
  return [...seen];
}

// ─── JSON path (same grammar the server resolves: a.b[0].c / a['x-y']) ───────

const SIMPLE_KEY = /^[A-Za-z_][A-Za-z0-9_]*$/;

export function childPath(parent: string, key: string | number): string {
  if (typeof key === 'number') return `${parent}[${key}]`;
  return SIMPLE_KEY.test(key) ? (parent ? `${parent}.${key}` : key) : `${parent}['${key.replace(/'/g, '')}']`;
}

/** Resolve a path against parsed JSON (client-side preview of what the server maps). */
export function resolveJsonPath(root: unknown, path: string): unknown {
  const tokens: Array<string | number> = [];
  const re = /\[(\d+)\]|\['([^']*)'\]|\["([^"]*)"\]|([^.[\]]+)/g;
  const cleaned = path.trim().replace(/^\$\.?/, '');
  for (const m of cleaned.matchAll(re)) {
    if (m[1] !== undefined) tokens.push(Number(m[1]));
    else tokens.push((m[2] ?? m[3] ?? m[4]) as string);
  }
  if (!tokens.length) return undefined;
  let cur: any = root;
  for (const t of tokens) {
    if (cur == null) return undefined;
    cur = typeof t === 'number' ? (Array.isArray(cur) ? cur[t] : undefined) : typeof cur === 'object' ? cur[t] : undefined;
  }
  return cur;
}

/** A readable variable name for a clicked path, unique among `taken`. */
export function suggestVariableName(path: string, taken: Set<string>): string {
  const last = path.replace(/\[\d+\]/g, '').split(/[.'\]\[]+/).filter(Boolean).pop() ?? 'value';
  let base = last.replace(/[^A-Za-z0-9_]/g, '_').replace(/^(\d)/, '_$1') || 'value';
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}${i}`)) i++;
  base = `${base}${i}`;
  return base;
}

/** Same rule the server enforces for flow-variable names. */
export function isValidVariableName(name: string): boolean {
  return /^[A-Za-z_][A-Za-z0-9_]{0,59}$/.test(name);
}

// ─── Response field picker ───────────────────────────────────────────────────

export interface ResponseField {
  /** Path in the same grammar the server resolves (`data[0].id`). */
  path: string;
  value: unknown;
  type: 'string' | 'number' | 'boolean' | 'null';
}

/**
 * Every pickable (leaf) value in a parsed response, depth-first, so the mapping UI
 * can offer a dropdown of real fields instead of asking the user to type a path.
 * Bounded: arrays contribute their first few items and the whole list is capped, so
 * a huge response can never freeze the page.
 */
export function flattenJsonLeaves(root: unknown, opts: { maxItems?: number; maxArray?: number; maxDepth?: number } = {}): ResponseField[] {
  const maxItems = opts.maxItems ?? 300;
  const maxArray = opts.maxArray ?? 5;
  const maxDepth = opts.maxDepth ?? 8;
  const out: ResponseField[] = [];
  const walk = (v: unknown, path: string, depth: number) => {
    if (out.length >= maxItems || depth > maxDepth) return;
    if (v === null) return void (path && out.push({ path, value: null, type: 'null' }));
    if (Array.isArray(v)) return v.slice(0, maxArray).forEach((x, i) => walk(x, childPath(path, i), depth + 1));
    if (typeof v === 'object') {
      return Object.entries(v as Record<string, unknown>).forEach(([k, x]) => walk(x, childPath(path, k), depth + 1));
    }
    if (!path) return; // a bare scalar response has no path to point at
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out.push({ path, value: v, type: typeof v as ResponseField['type'] });
    }
  };
  walk(root, '', 0);
  return out;
}

/** Short, single-line rendering of a sampled value for the picker / previews. */
export function previewValue(v: unknown, max = 40): string {
  const s = typeof v === 'string' ? `"${v}"` : v === undefined ? '—' : String(v);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

/** The endpoint with sample values substituted — the live "Request preview" line. */
export function renderPreviewUrl(endpoint: string, vars: Record<string, string>): string {
  return endpoint.replace(TOKEN_RE, (whole, name: string) => {
    const v = vars[name.trim()];
    return v !== undefined && v !== '' ? encodeURIComponent(v) : whole;
  });
}
